import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { media } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, X, Plus, Minus, Trash2, Mail, Save, Package, Truck,
  CreditCard, MapPin, StickyNote, History, Receipt, ExternalLink,
} from "lucide-react";

const STATUSES = ["awaiting_payment", "payment_submitted", "paid", "fulfilled", "cancelled"];
const STATUS_META = {
  awaiting_payment: { label: "Awaiting payment", cls: "bg-amber-400/15 text-amber-300 border-amber-400/30" },
  payment_submitted: { label: "Payment submitted", cls: "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" },
  paid: { label: "Paid", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  fulfilled: { label: "Fulfilled", cls: "bg-emerald-400/15 text-emerald-300 border-emerald-400/30" },
  cancelled: { label: "Cancelled", cls: "bg-rose-400/15 text-rose-300 border-rose-400/30" },
};
const CARRIERS = ["", "USPS", "UPS", "FedEx", "DHL", "Other"];

const Badge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.awaiting_payment;
  return <span className={`text-[11px] font-mono px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
};

export default function OrdersManager() {
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status = filter;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/invoice/admin/orders", { params });
      setOrders(data);
    } catch { setOrders([]); }
    setLoading(false);
  };
  const loadCounts = async () => {
    try {
      const { data } = await api.get("/invoice/admin/orders");
      const c = { all: data.length };
      data.forEach((o) => { c[o.status] = (c[o.status] || 0) + 1; });
      setCounts(c);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { loadCounts(); }, []);
  const refresh = () => { load(); loadCounts(); };

  return (
    <div data-testid="orders-manager">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <h1 className="font-serif text-4xl text-white">Orders</h1>
          <p className="text-slate-400 text-sm mt-1">Open an order to edit items, shipping, payment and fulfilment — then resend the invoice.</p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="orders-search"
            placeholder="Search invoice, email, tracking…"
            className="pl-9 w-72 bg-black/40 border-white/10 text-sm" />
        </form>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`orders-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-mono border transition ${filter === s ? "bg-cyan-400 text-black border-cyan-400" : "border-white/15 text-slate-300 hover:border-cyan-400/50"}`}>
            {(STATUS_META[s]?.label || "All")}{counts[s] != null ? ` · ${counts[s]}` : ""}
          </button>
        ))}
      </div>

      {loading && <div className="text-slate-500 font-mono text-sm animate-pulse">Loading orders…</div>}
      {!loading && !orders.length && <div className="glass rounded-2xl p-10 text-center text-slate-500 text-sm">No orders found.</div>}

      <div className="space-y-3">
        {orders.map((o) => (
          <button key={o.invoice_number} onClick={() => setActive(o.invoice_number)} data-testid={`order-row-${o.invoice_number}`}
            className="w-full text-left rounded-2xl border border-white/10 hover:border-cyan-400/40 bg-white/[0.02] p-4 transition">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm text-white">{o.invoice_number}</span>
              <span className="text-xs text-slate-400">{o.email}</span>
              <span className="text-sm font-mono text-cyan-300">${(o.total || 0).toFixed(2)}</span>
              <Badge status={o.status} />
              {o.fulfillment?.tracking_number && <span className="text-[11px] text-slate-500 flex items-center gap-1"><Truck size={12} /> {o.fulfillment.tracking_number}</span>}
              <span className="flex-1" />
              <span className="text-[11px] font-mono text-slate-600">{new Date(o.created_at).toLocaleDateString()}</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-2 truncate">{(o.items || []).map((i) => `${i.name} ×${i.qty}`).join("  ·  ")}</div>
          </button>
        ))}
      </div>

      {active && <OrderEditor num={active} onClose={() => setActive(null)} onSaved={refresh} />}
    </div>
  );
}

function OrderEditor({ num, onClose, onSaved }) {
  const [o, setO] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(false);
  const [adding, setAdding] = useState("");

  useEffect(() => {
    api.get(`/invoice/admin/orders/${num}`).then(({ data }) => setO(normalize(data))).catch(() => toast.error("Could not load order"));
    api.get("/invoice/admin/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, [num]);

  const normalize = (d) => ({
    ...d,
    shipping: d.shipping || {},
    fulfillment: d.fulfillment || {},
    admin_notes: d.admin_notes || "",
    payment_reference: d.payment_reference || "",
    override_on: d.price_override != null,
  });

  const recompute = (items) => {
    const qty = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce((s, i) => s + i.line, 0);
    const pct = qty >= 50 ? 30 : qty >= 25 ? 22 : qty >= 10 ? 15 : qty >= 5 ? 10 : 0;
    const discount = +(subtotal * pct / 100).toFixed(2);
    return { subtotal: +subtotal.toFixed(2), bulk_pct: pct, bulk_discount: discount, total: +(subtotal - discount).toFixed(2) };
  };

  const setItemQty = (idx, qty) => {
    if (qty < 1) return;
    const items = o.items.map((it, i) => i === idx ? { ...it, qty, line: +(it.variant.price * qty).toFixed(2) } : it);
    setO({ ...o, items, ...(o.override_on ? {} : recompute(items)) });
  };
  const removeItem = (idx) => {
    const items = o.items.filter((_, i) => i !== idx);
    setO({ ...o, items, ...(o.override_on ? {} : recompute(items)) });
  };
  const addItem = (variantId) => {
    const c = catalog.find((x) => x.variant_id === variantId);
    if (!c) return;
    const items = [...o.items, {
      variant: { id: c.variant_id, price: c.price, label: c.label, product_slug: "" },
      qty: 1, line: c.price, name: c.name, image: c.image,
    }];
    setO({ ...o, items, ...(o.override_on ? {} : recompute(items)) });
    setAdding("");
  };

  const upd = (k, v) => setO({ ...o, [k]: v });
  const updShip = (k, v) => setO({ ...o, shipping: { ...o.shipping, [k]: v } });
  const updFul = (k, v) => setO({ ...o, fulfillment: { ...o.fulfillment, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        email: o.email,
        status: o.status,
        payment_method: o.payment_method || null,
        payment_reference: o.payment_reference,
        shipping: o.shipping,
        fulfillment: o.fulfillment,
        admin_notes: o.admin_notes,
        items: o.items.map((i) => ({ variant_id: i.variant.id, quantity: i.qty })),
      };
      if (o.override_on) payload.total_override = Number(o.total) || 0;
      const { data } = await api.put(`/invoice/admin/orders/${num}`, payload);
      setO(normalize(data.order));
      toast.success("Order saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const resend = async (template = "invoice") => {
    setResending(true);
    try {
      await api.post(`/invoice/admin/orders/${num}/resend`, { origin: window.location.origin, template });
      toast.success(`${template === "invoice" ? "Invoice" : template} email sent to ${o.email}`);
      api.get(`/invoice/admin/orders/${num}`).then(({ data }) => setO(normalize(data)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Email failed"); }
    finally { setResending(false); }
  };

  if (!o) return (
    <Drawer onClose={onClose}><div className="p-10 text-center text-cyan-300 font-mono animate-pulse">Loading…</div></Drawer>
  );

  return (
    <Drawer onClose={onClose}>
      <div className="sticky top-0 z-10 bg-[#0b0f1a]/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center gap-3">
        <Receipt size={18} className="text-cyan-300" />
        <div>
          <div className="font-mono text-white text-sm">{o.invoice_number}</div>
          <div className="text-[11px] text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
        </div>
        <Badge status={o.status} />
        <span className="flex-1" />
        <button onClick={onClose} data-testid="order-editor-close" className="p-2 text-slate-400 hover:text-white"><X size={18} /></button>
      </div>

      <div className="p-6 space-y-6">
        {/* Line items */}
        <Section icon={<Package size={14} />} title="Line items">
          <div className="space-y-2">
            {o.items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3 rounded-xl border border-white/10 p-2.5" data-testid={`order-item-${idx}`}>
                {it.image ? <img src={media(it.image)} alt="" className="w-10 h-10 rounded-lg object-cover bg-black/40" /> : <div className="w-10 h-10 rounded-lg bg-black/40" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{it.name}</div>
                  <div className="text-[11px] font-mono text-slate-500">${it.variant.price.toFixed(2)} each</div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setItemQty(idx, it.qty - 1)} data-testid={`order-item-dec-${idx}`} className="p-1 rounded bg-white/5 text-slate-300 hover:bg-white/10"><Minus size={13} /></button>
                  <span className="w-7 text-center font-mono text-sm text-white">{it.qty}</span>
                  <button onClick={() => setItemQty(idx, it.qty + 1)} data-testid={`order-item-inc-${idx}`} className="p-1 rounded bg-white/5 text-slate-300 hover:bg-white/10"><Plus size={13} /></button>
                </div>
                <div className="w-20 text-right font-mono text-sm text-cyan-300">${it.line.toFixed(2)}</div>
                <button onClick={() => removeItem(idx)} data-testid={`order-item-remove-${idx}`} className="p-1.5 text-slate-500 hover:text-rose-400"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <select value={adding} onChange={(e) => { setAdding(e.target.value); if (e.target.value) addItem(e.target.value); }}
              data-testid="order-add-item" className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-slate-200 text-xs">
              <option value="">+ Add a product…</option>
              {catalog.map((c) => <option key={c.variant_id} value={c.variant_id}>{c.name} — ${c.price.toFixed(2)}</option>)}
            </select>
          </div>
        </Section>

        {/* Totals + override */}
        <Section icon={<CreditCard size={14} />} title="Totals & payment">
          <div className="rounded-xl border border-white/10 p-4 space-y-2">
            <Row l="Subtotal" v={`$${(o.subtotal || 0).toFixed(2)}`} />
            {o.bulk_pct > 0 && <Row l={`Bulk discount (${o.bulk_pct}%)`} v={`-$${(o.bulk_discount || 0).toFixed(2)}`} em />}
            <div className="flex items-center justify-between pt-1 border-t border-white/10">
              <label className="text-xs text-slate-400 flex items-center gap-2">
                <input type="checkbox" checked={o.override_on} data-testid="order-override-toggle"
                  onChange={(e) => setO({ ...o, override_on: e.target.checked, ...(e.target.checked ? {} : recompute(o.items)) })} />
                Manual total override
              </label>
              {o.override_on ? (
                <div className="flex items-center gap-1">
                  <span className="text-cyan-300">$</span>
                  <Input type="number" value={o.total} data-testid="order-total-override"
                    onChange={(e) => upd("total", e.target.value)} className="w-28 h-8 bg-black/40 border-white/15 text-right font-mono text-cyan-300" />
                </div>
              ) : <span className="font-mono text-lg text-cyan-300" data-testid="order-total">${(o.total || 0).toFixed(2)}</span>}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <Field label="Payment status">
              <select value={o.status} onChange={(e) => upd("status", e.target.value)} data-testid="order-status-select"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-slate-200 text-sm">
                {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
              </select>
            </Field>
            <Field label="Payment method">
              <Input value={o.payment_method || ""} onChange={(e) => upd("payment_method", e.target.value)} data-testid="order-payment-method"
                placeholder="paypal / venmo / zelle…" className="bg-black/40 border-white/15" />
            </Field>
            <Field label="Payment reference / transaction ID" full>
              <Input value={o.payment_reference} onChange={(e) => upd("payment_reference", e.target.value)} data-testid="order-payment-ref"
                placeholder="e.g. PayPal transaction ID" className="bg-black/40 border-white/15" />
            </Field>
          </div>
        </Section>

        {/* Customer + shipping */}
        <Section icon={<MapPin size={14} />} title="Customer & shipping">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Customer email" full>
              <Input value={o.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid="order-email" className="bg-black/40 border-white/15" />
            </Field>
            <Field label="Recipient name"><Input value={o.shipping.name || ""} onChange={(e) => updShip("name", e.target.value)} data-testid="order-ship-name" className="bg-black/40 border-white/15" /></Field>
            <Field label="Phone"><Input value={o.shipping.phone || ""} onChange={(e) => updShip("phone", e.target.value)} className="bg-black/40 border-white/15" /></Field>
            <Field label="Address line 1" full><Input value={o.shipping.address1 || ""} onChange={(e) => updShip("address1", e.target.value)} data-testid="order-ship-addr1" className="bg-black/40 border-white/15" /></Field>
            <Field label="Address line 2" full><Input value={o.shipping.address2 || ""} onChange={(e) => updShip("address2", e.target.value)} className="bg-black/40 border-white/15" /></Field>
            <Field label="City"><Input value={o.shipping.city || ""} onChange={(e) => updShip("city", e.target.value)} className="bg-black/40 border-white/15" /></Field>
            <Field label="State / region"><Input value={o.shipping.state || ""} onChange={(e) => updShip("state", e.target.value)} className="bg-black/40 border-white/15" /></Field>
            <Field label="ZIP / postal"><Input value={o.shipping.zip || ""} onChange={(e) => updShip("zip", e.target.value)} className="bg-black/40 border-white/15" /></Field>
            <Field label="Country"><Input value={o.shipping.country || ""} onChange={(e) => updShip("country", e.target.value)} className="bg-black/40 border-white/15" /></Field>
          </div>
        </Section>

        {/* Fulfillment */}
        <Section icon={<Truck size={14} />} title="Fulfilment & tracking">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Carrier">
              <select value={o.fulfillment.carrier || ""} onChange={(e) => updFul("carrier", e.target.value)} data-testid="order-carrier"
                className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-slate-200 text-sm">
                {CARRIERS.map((c) => <option key={c} value={c}>{c || "— select —"}</option>)}
              </select>
            </Field>
            <Field label="Tracking number"><Input value={o.fulfillment.tracking_number || ""} onChange={(e) => updFul("tracking_number", e.target.value)} data-testid="order-tracking" className="bg-black/40 border-white/15" /></Field>
            <Field label="Tracking URL (https)" full><Input value={o.fulfillment.tracking_url || ""} onChange={(e) => updFul("tracking_url", e.target.value)} placeholder="https://…" className="bg-black/40 border-white/15" /></Field>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Adding a new tracking number emails the customer a shipment notification on save.</p>
        </Section>

        {/* Notes */}
        <Section icon={<StickyNote size={14} />} title="Internal notes">
          <textarea value={o.admin_notes} onChange={(e) => upd("admin_notes", e.target.value)} rows={3} data-testid="order-notes"
            placeholder="Private notes — never shown to the customer." className="w-full bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-slate-200 text-sm" />
        </Section>

        {/* Activity */}
        {!!(o.activity || []).length && (
          <Section icon={<History size={14} />} title="Activity">
            <div className="space-y-1.5">
              {[...o.activity].reverse().map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px]">
                  <span className="font-mono text-slate-600 shrink-0">{new Date(a.at).toLocaleString()}</span>
                  <span className="text-slate-300">{a.detail || a.action}</span>
                  <span className="text-slate-600">· {a.actor}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
      </div>

      {/* Sticky actions */}
      <div className="sticky bottom-0 bg-[#0b0f1a]/95 backdrop-blur border-t border-white/10 px-6 py-4 flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="order-save" className="btn-primary rounded-xl">
          <Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save changes"}
        </Button>
        <Button onClick={() => resend("invoice")} disabled={resending} data-testid="order-resend" variant="outline" className="rounded-xl border-white/15 text-slate-200">
          <Mail size={15} className="mr-1.5" /> {resending ? "Sending…" : "Resend invoice"}
        </Button>
        <a href={`/invoice/${o.invoice_number}`} target="_blank" rel="noreferrer" data-testid="order-view-invoice"
          className="text-xs font-mono text-slate-400 hover:text-cyan-300 flex items-center gap-1 ml-auto">
          View invoice page <ExternalLink size={12} />
        </a>
      </div>
    </Drawer>
  );
}

const Drawer = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex justify-end" data-testid="order-editor">
    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
    <div className="relative w-full max-w-2xl h-full overflow-y-auto bg-[#0b0f1a] border-l border-white/10 shadow-2xl">
      {children}
    </div>
  </div>
);

const Section = ({ icon, title, children }) => (
  <div>
    <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-cyan-300 mb-2.5">{icon} {title}</div>
    {children}
  </div>
);

const Field = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="text-[11px] text-slate-400 mb-1">{label}</div>
    {children}
  </div>
);

const Row = ({ l, v, em }) => (
  <div className="flex justify-between text-sm">
    <span className="text-slate-400">{l}</span>
    <span className={`font-mono ${em ? "text-emerald-400" : "text-slate-200"}`}>{v}</span>
  </div>
);
